import React from 'react'
import styled from '@emotion/styled'
import { ScheduleOverride, OverrideType } from '../types'
import services from '@services'
import {
  OVERRIDE_TYPE_LABELS,
  ADD_REASON_LABELS,
  CANCEL_REASON_LABELS,
  DAY_STATUS_COLORS,
  DAY_STATUS_BORDER_COLORS,
} from '../constants'
import { formatCutoffLabel, formatDropoffLabel } from '../utils'
import { AddReason, CancelReason } from '../types'

interface Props {
  overrides: ScheduleOverride[]
  onRemove: (id: string) => void
}

function getReasonLabel(override: ScheduleOverride): string {
  if (!override.reason) {
    return ''
  }
  if (override.overrideType === OverrideType.Add) {
    return ADD_REASON_LABELS[override.reason as AddReason] || override.reason
  }

  return (
    CANCEL_REASON_LABELS[override.reason as CancelReason] || override.reason
  )
}

function getStatusForOverride(
  type: OverrideType,
): 'ad-hoc-on' | 'cancelled' | 'service' {
  switch (type) {
    case OverrideType.Add:
      return 'ad-hoc-on'
    case OverrideType.Cancel:
      return 'cancelled'
    default:
      return 'service'
  }
}

const OverridesList: React.FC<Props> = ({ overrides, onRemove }) => {
  if (overrides.length === 0) {
    return <EmptyText>No overrides configured.</EmptyText>
  }

  const sorted = [...overrides].sort((a, b) => a.date.localeCompare(b.date))

  return (
    <List>
      {sorted.map((override) => {
        const status = getStatusForOverride(override.overrideType)
        const reasonLabel = getReasonLabel(override)

        return (
          <OverrideItem
            key={override.id}
            data-testid={`override-item-${override.id}`}
            borderColor={DAY_STATUS_BORDER_COLORS[status]}
            bgColor={DAY_STATUS_COLORS[status]}
          >
            <ItemHeader>
              <DateLabel>{override.date}</DateLabel>
              <TypeBadge>
                {OVERRIDE_TYPE_LABELS[override.overrideType]}
              </TypeBadge>
              <RemoveButton
                onClick={async () => {
                  const confirmed =
                    await services.UIService.ConfirmationModal.show({
                      title: 'Remove Override',
                      text: `Are you sure you want to remove the override for ${override.date}?`,
                    })
                  if (confirmed) {
                    onRemove(override.id)
                  }
                }}
                title="Remove override"
                data-testid={`remove-override-${override.id}`}
              >
                ×
              </RemoveButton>
            </ItemHeader>
            {reasonLabel && <ReasonText>{reasonLabel}</ReasonText>}
            {override.notes && <NotesText>{override.notes}</NotesText>}
            {override.cutoffDaysBefore != null &&
              override.cutoffTime != null && (
                <CutoffText>
                  Cutoff:{' '}
                  {formatCutoffLabel({
                    cutoffDaysBefore: override.cutoffDaysBefore,
                    cutoffTime: override.cutoffTime,
                  })}
                </CutoffText>
              )}
            {override.dropoffTime != null && (
              <DropoffText>
                Dropoff: {formatDropoffLabel(override.dropoffTime)}
              </DropoffText>
            )}
          </OverrideItem>
        )
      })}
    </List>
  )
}

export default OverridesList

// ── Styled Components ──

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const OverrideItem = styled.div<{ borderColor: string; bgColor: string }>`
  padding: 10px 12px;
  border-left: 4px solid ${({ borderColor }) => borderColor};
  background: ${({ bgColor }) => bgColor};
  border-radius: 0 4px 4px 0;
`

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DateLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #212121;
`

const TypeBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.08);
  color: #424242;
`

const RemoveButton = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  font-size: 18px;
  color: #9e9e9e;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    color: #f44336;
  }
`

const ReasonText = styled.div`
  font-size: 12px;
  color: #616161;
  margin-top: 4px;
`

const NotesText = styled.div`
  font-size: 12px;
  color: #757575;
  font-style: italic;
  margin-top: 2px;
`

const CutoffText = styled.div`
  font-size: 11px;
  color: #e65100;
  margin-top: 4px;
  font-weight: 500;
`

const DropoffText = styled.div`
  font-size: 11px;
  color: #1565c0;
  margin-top: 2px;
  font-weight: 500;
`

const EmptyText = styled.p`
  color: #9e9e9e;
  font-size: 13px;
  font-style: italic;
`
